/**
 * Shop quality-of-life: Merkliste (per-device saved list) + recently
 * viewed. Pure progressive enhancement — every entry point ships hidden
 * and is unhidden here; zero-JS visitors never see a dead control.
 *
 * No money is ever formatted in JS: product rows are fetched as
 * server-rendered fragments via the Section Rendering API
 * (/products/<handle>?section_id=merk-item).
 */
(() => {
  if (window.__bunzQol) return;
  window.__bunzQol = true;

  const MERK_KEY = 'bunz:merkliste';
  const RECENT_KEY = 'bunz:recent';

  const read = (key) => {
    try {
      const v = JSON.parse(localStorage.getItem(key) || '[]');
      return Array.isArray(v) ? v.filter((h) => typeof h === 'string') : [];
    } catch { return []; }
  };
  const store = (key, list) => {
    try { localStorage.setItem(key, JSON.stringify(list)); } catch {}
  };

  /* ── fetch one server-rendered row ── */
  const fetchRow = async (handle) => {
    try {
      // locale root ("/en/" on the English storefront), so rows render in
      // the shopper's language and link to the same-language product page
      const root = window.Shopify?.routes?.root || '/';
      const res = await fetch(`${root}products/${handle}?section_id=merk-item`);
      if (!res.ok) return null;
      const html = await res.text();
      // trust boundary: same-origin Section Rendering API response — our
      // own merk-item section, all fields Liquid-escaped server-side
      const tpl = document.createElement('template');
      tpl.innerHTML = html;
      return tpl.content.querySelector('.merk-item');
    } catch { return null; }
  };

  /* ── Merkliste store + hearts + header count ── */
  const merk = new Set(read(MERK_KEY));
  const save = () => {
    store(MERK_KEY, [...merk]);
    syncCount();
    syncHearts();
  };
  const syncCount = () => {
    document.querySelectorAll('[data-merk-count]').forEach((el) => {
      el.textContent = merk.size;
      el.hidden = merk.size === 0;
    });
  };
  const syncHearts = () => {
    document.querySelectorAll('[data-merk]').forEach((btn) => {
      const saved = merk.has(btn.dataset.merkHandle);
      btn.hidden = false;
      btn.classList.toggle('is-saved', saved);
      btn.setAttribute('aria-pressed', String(saved));
      const label = saved ? btn.dataset.labelRemove : btn.dataset.labelAdd;
      if (label) btn.setAttribute('aria-label', label);
    });
  };

  document.addEventListener('click', (e) => {
    const heart = e.target.closest('[data-merk]');
    if (heart) {
      const h = heart.dataset.merkHandle;
      merk.has(h) ? merk.delete(h) : merk.add(h);
      save();
      return;
    }
    const remove = e.target.closest('[data-merk-remove]');
    if (remove) {
      e.preventDefault();
      merk.delete(remove.dataset.merkHandle);
      save();
      const row = remove.closest('.merk-item');
      if (row) row.remove();
      const grid = document.querySelector('[data-merkliste-grid]');
      const empty = document.querySelector('[data-merkliste-empty]');
      if (grid && empty && !grid.querySelector('.merk-item')) {
        grid.hidden = true;
        empty.hidden = false;
      }
    }
  });

  /* ── Merkliste page ── */
  const renderList = async () => {
    const grid = document.querySelector('[data-merkliste-grid]');
    const empty = document.querySelector('[data-merkliste-empty]');
    if (!grid || merk.size === 0) return;
    const rows = (await Promise.all([...merk].slice(0, 24).map(fetchRow))).filter(Boolean);
    if (!rows.length) return;
    for (const row of rows) {
      const btn = row.querySelector('[data-merk-remove]');
      if (btn) btn.hidden = false;
      grid.appendChild(row);
    }
    grid.hidden = false;
    if (empty) empty.hidden = true;
  };

  /* ── recently viewed (PDP) ── */
  const recently = async () => {
    const section = document.querySelector('[data-recently]');
    if (!section) return;
    const current = section.dataset.recentlyCurrent;
    const seen = read(RECENT_KEY).filter((h) => h !== current);
    const grid = section.querySelector('[data-recently-grid]');
    if (grid && seen.length) {
      const rows = (await Promise.all(seen.slice(0, 4).map(fetchRow))).filter(Boolean);
      if (rows.length) {
        rows.forEach((r) => grid.appendChild(r));
        section.hidden = false;
      }
    }
    store(RECENT_KEY, [current, ...seen].slice(0, 8));
  };

  const init = () => {
    syncCount();
    syncHearts();
    renderList();
    recently();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // cards swapped in by the filters (facets.js) arrive with hidden hearts
  document.addEventListener('bunz:content-updated', syncHearts);
})();
